import React, { useEffect, useState, useRef } from 'react';
import { Box, Container, Paper, TextField, Button, MenuItem, Select, InputLabel, FormControl, Typography, Grid, Card, CardContent } from '@mui/material';
import DraggableTable from '../components/DraggableTable';
import buxferTransactions from '../services/buxfer.service';

export default function BuxferSyncHistoryPage() {
  const initialParamsApplied = useRef(false);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [minTxCount, setMinTxCount] = useState('');
  const [maxTxCount, setMaxTxCount] = useState('');
  const [orderBy, setOrderBy] = useState('transaction_count');
  const [orderDirection, setOrderDirection] = useState('desc');
  const [summary, setSummary] = useState(null);

  const updateQueryParams = () => {
    const params = new URLSearchParams();
    params.set('page', page + 1);
    params.set('rowsPerPage', rowsPerPage);
    if (keyword) params.set('keyword', keyword);
    if (accountName) params.set('accountName', accountName);
    if (email) params.set('buxaccount_email', email);
    if (status) params.set('status', status);
    if (dateFrom) params.set('dateFrom', dateFrom);
    if (dateTo) params.set('dateTo', dateTo);
    if (minTxCount) params.set('minTxCount', minTxCount);
    if (maxTxCount) params.set('maxTxCount', maxTxCount);
    if (orderBy) params.set('orderBy', orderBy);
    if (orderDirection) params.set('orderDirection', orderDirection);
    window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
  };

  const columns = [
    { id: 'date_synced', label: 'Date Synced', sortable: true, sortKey: 'date_synced' },
    { id: 'account_name', label: 'Account Name', sortable: true, sortKey: 'account_name' },
    { id: 'buxaccount_email', label: 'Email', sortable: true, sortKey: 'buxaccount_email' },
    { id: 'transaction_count', label: 'Transactions', sortable: true, sortKey: 'transaction_count' },
    { id: 'status', label: 'Status', sortable: true, sortKey: 'status' },
    { id: 'notes', label: 'Notes', sortable: false }
  ];

  // 👈 Helper: Get columns with saved order applied
  const getDisplayedColumns = () => {
    try {
      const savedOrder = localStorage.getItem('buxfer_sync_history_column_order');
      if (savedOrder) {
        const orderArray = JSON.parse(savedOrder);
        // Sort columns by saved order
        return columns.sort((a, b) => {
          const aIndex = orderArray.indexOf(a.id);
          const bIndex = orderArray.indexOf(b.id);
          if (aIndex === -1 && bIndex === -1) return 0;
          if (aIndex === -1) return 1;
          if (bIndex === -1) return -1;
          return aIndex - bIndex;
        });
      }
    } catch (e) {
      console.error('Failed to apply saved column order', e);
    }
    return columns;
  };

  const handleColumnsReorder = (reorderedColumns) => {
    // Save the new column order to page-specific localStorage
    try {
      const columnOrder = reorderedColumns.map(col => col.id);
      localStorage.setItem('buxfer_sync_history_column_order', JSON.stringify(columnOrder));
    } catch (e) {
      console.error('Failed to save column order', e);
    }
  };

  const fetchData = async () => {
    try {
      const resp = await buxferTransactions.getSyncHistory({
        page: page + 1,
        pageSize: rowsPerPage,
        keyword,
        accountName,
        buxaccount_email: email,
        status,
        dateFrom,
        dateTo,
        minTxCount,
        maxTxCount,
        orderBy,
        orderDirection
      });
      if (resp && resp.success) {
        setRows(resp.result.rows || []);
        setTotalCount(resp.result.count || 0);
      }
    } catch (err) {
      console.error('Failed to load sync history', err);
      setRows([]);
      setTotalCount(0);
    }
  };

  const fetchSummary = async () => {
    try {
      const resp = await buxferTransactions.getSyncSummary();
      if (resp && resp.success) {
        setSummary(resp.result);
      }
    } catch (err) {
      console.error('Failed to load sync summary', err);
      setSummary(null);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  useEffect(() => {
    // Skip automatic fetch until we've applied any initial URL params
    if (!initialParamsApplied.current) return;
    fetchData();
    // Keep URL in sync after initial params applied
    updateQueryParams();
  }, [page, rowsPerPage, orderBy, orderDirection, keyword, accountName, email, status, dateFrom, dateTo, minTxCount, maxTxCount]);

  // Parse URL params once on mount and do a single initial fetch
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageParam = parseInt(params.get('page'), 10);
    const rowsParam = parseInt(params.get('rowsPerPage'), 10);
    const keywordParam = params.get('keyword') || '';
    const accountParam = (params.get('accountName') || '').trim();
    const emailParam = (params.get('buxaccount_email') || '').trim();
    const statusParam = params.get('status') || '';
    const dateFromParam = params.get('dateFrom') || '';
    const dateToParam = params.get('dateTo') || '';
    const minTxParam = params.get('minTxCount') || '';
    const maxTxParam = params.get('maxTxCount') || '';
    const orderByParam = params.get('orderBy') || orderBy;
    const orderDirParam = (params.get('orderDirection') || orderDirection).toLowerCase();

    if (!isNaN(pageParam)) setPage(pageParam - 1);
    if (!isNaN(rowsParam)) setRowsPerPage(rowsParam);
    setKeyword(keywordParam);
    setAccountName(accountParam);
    setEmail(emailParam);
    setStatus(statusParam);
    setDateFrom(dateFromParam);
    setDateTo(dateToParam);
    setMinTxCount(minTxParam);
    setMaxTxCount(maxTxParam);
    setOrderBy(orderByParam);
    setOrderDirection(orderDirParam);

    // mark applied and perform one fetch with the parsed params
    initialParamsApplied.current = true;
    (async () => {
      try {
        const resp = await buxferTransactions.getSyncHistory({
          page: (!isNaN(pageParam) ? pageParam : 1),
          pageSize: (!isNaN(rowsParam) ? rowsParam : rowsPerPage),
          keyword: keywordParam,
          accountName: accountParam,
          buxaccount_email: emailParam,
          status: statusParam,
          dateFrom: dateFromParam,
          dateTo: dateToParam,
          minTxCount: minTxParam,
          maxTxCount: maxTxParam,
          orderBy: orderByParam,
          orderDirection: orderDirParam
        });
        if (resp && resp.success) {
          setRows(resp.result.rows || []);
          setTotalCount(resp.result.count || 0);
        }
      } catch (err) {
        console.error('Initial fetch failed', err);
      }
    })();
  }, []);

  const handleSortChange = (key) => {
    if (orderBy === key) {
      setOrderDirection(orderDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setOrderBy(key);
      setOrderDirection('asc');
    }
    setPage(0);
    fetchData();
  };

  const handleSummaryClick = (type) => {
    setPage(0);
    if (type === 'total') {
      setStatus('');
      setDateFrom('');
      setDateTo('');
    } else if (type === 'active') {
      setStatus('success');
      setDateFrom('');
      setDateTo('');
    } else if (type === 'notSynced24h') {
      setStatus('success');
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setDateTo(yesterday.toISOString().split('T')[0]);
      setDateFrom('');
    } else if (type === 'reauth') {
      setStatus('Requires re-authentication');
      setDateFrom('');
      setDateTo('');
    } else if (type === 'error') {
      setStatus('error');
      setDateFrom('');
      setDateTo('');
    }
    fetchData();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2 }}>
      {summary && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" gutterBottom>Sync Summary</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ cursor: 'pointer' }} onClick={() => handleSummaryClick('total')}>
                <CardContent>
                  <Typography variant="h5" component="div">{summary.totalAccounts}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Accounts</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ cursor: 'pointer' }} onClick={() => handleSummaryClick('active')}>
                <CardContent>
                  <Typography variant="h5" component="div">{summary.activeAccounts}</Typography>
                  <Typography variant="body2" color="text.secondary">Active Accounts</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ cursor: 'pointer' }} onClick={() => handleSummaryClick('notSynced24h')}>
                <CardContent>
                  <Typography variant="h5" component="div">{summary.notSynced24h}</Typography>
                  <Typography variant="body2" color="text.secondary">Not Synced (24h)</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ cursor: 'pointer' }} onClick={() => handleSummaryClick('reauth')}>
                <CardContent>
                  <Typography variant="h5" component="div">{summary.needReauth}</Typography>
                  <Typography variant="body2" color="text.secondary">Need Reauth</Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Card sx={{ cursor: 'pointer' }} onClick={() => handleSummaryClick('error')}>
                <CardContent>
                  <Typography variant="h5" component="div">{summary.hasError}</Typography>
                  <Typography variant="body2" color="text.secondary">Has Error</Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Paper>
      )}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField label="Keyword" value={keyword} onChange={(e) => setKeyword(e.target.value)} />
          <TextField label="Account Name" value={accountName} onChange={(e) => setAccountName(e.target.value)} />
          <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <TextField label="Date From" type="date" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <TextField label="Date To" type="date" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          <TextField label="Min Tx" type="number" value={minTxCount} onChange={(e) => setMinTxCount(e.target.value)} />
          <TextField label="Max Tx" type="number" value={maxTxCount} onChange={(e) => setMaxTxCount(e.target.value)} />

          <FormControl sx={{ minWidth: 160 }}>
            <InputLabel>Order By</InputLabel>
            <Select value={orderBy} label="Order By" onChange={(e) => setOrderBy(e.target.value)}>
              <MenuItem value="date_synced">Date</MenuItem>
              <MenuItem value="transaction_count">Transactions</MenuItem>
              <MenuItem value="account_name">Account Name</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel>Direction</InputLabel>
            <Select value={orderDirection} label="Direction" onChange={(e) => setOrderDirection(e.target.value)}>
              <MenuItem value="desc">DESC</MenuItem>
              <MenuItem value="asc">ASC</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" onClick={() => { setPage(0); fetchData(); }}>Search</Button>
            <Button variant="outlined" onClick={() => { setKeyword(''); setAccountName(''); setEmail(''); setDateFrom(''); setDateTo(''); setMinTxCount(''); setMaxTxCount(''); setPage(0); fetchData(); }}>Clear</Button>
          </Box>
        </Box>
      </Paper>

      <Paper>
        <DraggableTable
          data={rows}
          columns={getDisplayedColumns()}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          orderBy={orderBy}
          orderDirection={orderDirection}
          onPageChange={(e, newPage) => { setPage(newPage); fetchData(); }}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); fetchData(); }}
          onSortChange={handleSortChange}
          onColumnsReorder={handleColumnsReorder}
        />
      </Paper>
    </Container>
  );
}
